import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { formatAmount } from '@/lib/utils';
import {
  useGetRevenue,
  useGetRevenueByCompany,
  useGetRevenueByMachine,
} from '@/services/api/endpoints/AnalyticsService';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  type TooltipProps,
} from 'recharts';

const COLORS = ['#2563eb', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

function AmountTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-lg">
      <p className="mb-1 text-sm font-medium">{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-3 text-sm">
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}</span>
          <span className="ml-auto font-medium">
            {typeof entry.value === 'number' && entry.dataKey !== 'transactions'
              ? formatAmount(entry.value)
              : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function RevenueReportsPage() {
  const { t } = useTranslation();

  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: revenueData, isLoading: revenueLoading } = useGetRevenue(
    period, dateFrom || undefined, dateTo || undefined
  );
  const { data: byCompanyData, isLoading: companyLoading } = useGetRevenueByCompany(
    dateFrom || undefined, dateTo || undefined
  );
  const { data: byMachineData, isLoading: machineLoading } = useGetRevenueByMachine(
    dateFrom || undefined, dateTo || undefined
  );

  const revenueChartData = useMemo(() => {
    if (!revenueData) return [];
    return revenueData.map(item => ({
      ...item,
      dateFormatted: item.date.length > 7
        ? new Date(item.date).toLocaleDateString('sr-Latn', { day: 'numeric', month: 'short' })
        : item.date,
    }));
  }, [revenueData]);

  const totalRevenue = useMemo(() => {
    return revenueData?.reduce((sum, d) => sum + d.revenue, 0) || 0;
  }, [revenueData]);

  const totalTransactions = useMemo(() => {
    return revenueData?.reduce((sum, d) => sum + d.transactions, 0) || 0;
  }, [revenueData]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">{t('revenue.title')}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="w-40"
          />
          <span className="text-muted-foreground">-</span>
          <Input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="w-40"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t('revenue.totalRevenue')}</p>
            <p className="text-2xl font-bold mt-1">{formatAmount(totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t('revenue.totalTransactions')}</p>
            <p className="text-2xl font-bold mt-1">{totalTransactions.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t('revenue.avgTransaction')}</p>
            <p className="text-2xl font-bold mt-1">
              {formatAmount(totalTransactions > 0 ? totalRevenue / totalTransactions : 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Over Time */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">{t('revenue.revenueOverTime')}</CardTitle>
          <div className="flex gap-1">
            {(['daily', 'weekly', 'monthly'] as const).map((p) => (
              <Button
                key={p}
                variant={period === p ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPeriod(p)}
              >
                {t(`revenue.${p}`)}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {revenueLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : revenueChartData.length === 0 ? (
            <div className="h-72 flex items-center justify-center border border-dashed rounded-lg bg-muted/20 text-muted-foreground text-sm">
              {t('common.noResults')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <AreaChart data={revenueChartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="dateFormatted" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toString()}
                />
                <Tooltip content={<AmountTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name={t('revenue.revenue')}
                  stroke="#2563eb"
                  strokeWidth={2}
                  fill="url(#revGrad)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue By Company */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('revenue.revenueByCompany')}</CardTitle>
          </CardHeader>
          <CardContent>
            {companyLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : !byCompanyData?.length ? (
              <div className="h-64 flex items-center justify-center border border-dashed rounded-lg bg-muted/20 text-muted-foreground text-sm">
                {t('common.noResults')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={256}>
                <BarChart
                  data={byCompanyData.slice(0, 10)}
                  layout="vertical"
                  margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toString()}
                  />
                  <YAxis
                    type="category"
                    dataKey="companyName"
                    tick={{ fontSize: 11 }}
                    width={100}
                  />
                  <Tooltip content={<AmountTooltip />} />
                  <Bar dataKey="revenue" name={t('revenue.revenue')} fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Revenue By Machine */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('revenue.revenueByMachine')}</CardTitle>
          </CardHeader>
          <CardContent>
            {machineLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : !byMachineData?.length ? (
              <div className="h-64 flex items-center justify-center border border-dashed rounded-lg bg-muted/20 text-muted-foreground text-sm">
                {t('common.noResults')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={256}>
                <BarChart
                  data={byMachineData.slice(0, 10)}
                  layout="vertical"
                  margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toString()}
                  />
                  <YAxis
                    type="category"
                    dataKey="machineName"
                    tick={{ fontSize: 11 }}
                    width={100}
                  />
                  <Tooltip content={<AmountTooltip />} />
                  <Bar dataKey="revenue" name={t('revenue.revenue')} fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Transaction Volume */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('revenue.transactionVolume')}</CardTitle>
        </CardHeader>
        <CardContent>
          {revenueLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : revenueChartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center border border-dashed rounded-lg bg-muted/20 text-muted-foreground text-sm">
              {t('common.noResults')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={256}>
              <LineChart data={revenueChartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="dateFormatted" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip content={<AmountTooltip />} />
                <Line
                  type="monotone"
                  dataKey="transactions"
                  name={t('revenue.transactions')}
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
